import { useState, useEffect } from 'react';
import config from '@/config';
import { Logger } from '@shared/lib/logger';

const PWA_MODE = import.meta.env.MODE;

const logger = new Logger('sw.hooks');

// Declare the injected variables from Vite with fallbacks
declare const __VERSION_HASH__: string | undefined;
declare const __PACKAGE_VERSION__: string | undefined;
declare const __SW_VERSION__: string | undefined;

declare global {
	interface Window {
		sw: ServiceWorker | null;
	}
}

// Helper function to create version hash (fallback for development)
function createVersionHash(version: string): string {
	// Simple hash function for development fallback
	let hash = 0;
	for (let i = 0; i < version.length; i++) {
		const char = version.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash; // Convert to 32bit integer
	}
	return Math.abs(hash).toString(16).substring(0, 8);
}

/**
 * React hook that returns the navigator service worker with version-aware registration
 *
 * @return {ServiceWorker} The service worker
 */
export function useLoad(): ServiceWorker | null {
	const [sw, setSw] = useState<ServiceWorker | null>(null);

	useEffect(() => {
		// Skip service worker registration in development if PWA is disabled
		if (
			PWA_MODE === 'development' &&
			process.env.NODE_ENV === 'development'
		) {
			logger.info('Service Worker disabled in development mode');
			return;
		}

		if ('serviceWorker' in navigator) {
			registerServiceWorkerWithVersion();

			// Wait for service worker to be ready
			navigator.serviceWorker.ready
				.then((registration) => {
					logger.info(`SERVICE WORKER READY:`, registration);
					window.sw = registration.active;
					setSw(registration.active);
				})
				.catch((err) => {
					logger.error(`Service worker ready error`, err);
				});
		}
	}, []);

	async function registerServiceWorkerWithVersion() {
		if (!('serviceWorker' in navigator)) return;

		try {
			// Use injected version info with fallbacks
			const versionHash =
				typeof __VERSION_HASH__ !== 'undefined'
					? __VERSION_HASH__
					: createVersionHash(config.build.version);

			const packageVersion =
				typeof __PACKAGE_VERSION__ !== 'undefined'
					? __PACKAGE_VERSION__
					: config.build.version;

			const swVersion =
				typeof __SW_VERSION__ !== 'undefined'
					? __SW_VERSION__
					: `${packageVersion}-${versionHash}`;

			const host = window.location.protocol + '//' + window.location.host;
			const scopeUrl = config.build.subdirectory
				? `${config.build.subdirectory}`
				: '/';

			// Create service worker URL with version hash
			// In development, try different service worker URLs
			const isDevelopment = PWA_MODE === 'development';
			const swUrls = isDevelopment
				? ['/dev-sw.js?dev-sw', `/sw.js?v=${swVersion}`, '/sw.js']
				: [`/sw.js?v=${swVersion}`];

			logger.debug(`SW REGISTRATION WITH VERSION:`, {
				scopeUrl,
				swUrls,
				host,
				version: swVersion,
				hash: versionHash,
				mode: PWA_MODE,
				isDevelopment,
			});

			let registration = null;
			let lastError = null;

			// Try each URL until one works
			for (const swUrl of swUrls) {
				try {
					logger.debug(`Trying to register SW at: ${swUrl}`);
					registration = await navigator.serviceWorker.register(
						swUrl,
						{
							scope: scopeUrl,
						},
					);
					break;
				} catch (error) {
					logger.warn(`SW registration failed for ${swUrl}:`, error);
					lastError = error;
					continue;
				}
			}

			if (!registration) {
				throw (
					lastError ||
					new Error('All service worker registration attempts failed')
				);
			}

			logger.info(`SUCCESSFUL SW REGISTRATION:`, registration);

			// Handle service worker updates
			registration.addEventListener('updatefound', () => {
				const newWorker = registration.installing;
				logger.debug('New service worker found');

				if (newWorker) {
					newWorker.addEventListener('statechange', () => {
						if (
							newWorker.state === 'installed' &&
							navigator.serviceWorker.controller
						) {
							logger.info('New service worker version available');
							// You could emit an event here to notify the app about the update
						}
					});
				}
			});

			// Handle controller change (new SW activated)
			navigator.serviceWorker.addEventListener('controllerchange', () => {
				logger.debug('Service worker controller changed');
				// Update the sw state with the new controller
				setSw(navigator.serviceWorker.controller);
				window.sw = navigator.serviceWorker.controller;
			});
		} catch (error) {
			logger.error('SW registration failed:', error);
			// Don't try fallback in development - it will just fail again
			if (PWA_MODE !== 'development') {
				try {
					logger.info('Trying final fallback SW registration...');
					const fallbackUrl = `/sw.js?v=${config.build.version}`;
					const fallbackRegistration =
						await navigator.serviceWorker.register(fallbackUrl, {
							scope: config.build.subdirectory
								? `${config.build.subdirectory}`
								: '/',
						});
					logger.info(
						'Fallback SW registration successful:',
						fallbackRegistration,
					);
				} catch (fallbackError) {
					logger.error(
						'Fallback SW registration also failed:',
						fallbackError,
					);
				}
			}
		}
	}

	return sw;
}
